import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { NavLink, Redirect } from "react-router-dom";
import { Button, Divider, Dropdown, Form, Icon, Message, Segment } from "semantic-ui-react";
import AgenciesService from "../../../../services/agencies";
import CampaignsService from "../../../../services/campaign";
import ReportsService from "../../../../services/reports";
import T1Service from "../../../../services/t1";

const toDropdownOptions = (data) => data.map(({ id, title }) => ({ key: id, text: title, value: id }));

const loadList = async ({ fetcher, setItems, setLoading, intl, setError, errorId, errorDefault }) => {
  try {
    const resp = await fetcher();
    setItems(toDropdownOptions(resp.data));
  } catch (e) {
    setError(e?.error?.message || intl.formatMessage({ id: errorId, defaultMessage: errorDefault }));
  } finally {
    setLoading(false);
  }
};

const BrandLiftReportPage = () => {
  const intl = useIntl();
  const { whitelabelBrandLiftCampaign } = useFlags();

  const services = useRef(
    new Map([
      ["t1", new T1Service()],
      ["agencies", new AgenciesService()],
      ["campaigns", new CampaignsService()],
      ["reports", new ReportsService()],
    ]),
  );

  const [advertiserId, setAdvertiserId] = useState(null);
  const [campaignId, setCampaignId] = useState(null);
  const [advertisers, setAdvertisers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [advertisersLoading, setAdvertisersLoading] = useState(true);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [reportUrl, setReportUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadList({
      fetcher: () => {
        const agencyId = services.current.get("agencies").getSelectedAgency() || 0;
        return services.current.get("t1").advertisers(agencyId, "");
      },
      setItems: setAdvertisers,
      setLoading: setAdvertisersLoading,
      intl,
      setError,
      errorId: "FAILED_FETCH_DATA",
      errorDefault: "Failed to fetch data",
    });
  }, [intl]);

  useEffect(() => {
    setError(null);
    if (!advertiserId) {
      setCampaigns([]);
      setCampaignId(null);
      setReportUrl(null);
      return;
    }
    setCampaignsLoading(true);
    setCampaignId(null);
    setReportUrl(null);
    loadList({
      fetcher: () => services.current.get("campaigns").getBrandLiftCampaigns(advertiserId, true),
      setItems: setCampaigns,
      setLoading: setCampaignsLoading,
      intl,
      setError,
      errorId: "FAILED_FETCH_DATA",
      errorDefault: "Failed to fetch data",
    });
  }, [advertiserId, intl]);

  const handleViewReport = async () => {
    setLoading(true);
    setError(null);
    setReportUrl(null);
    try {
      const resp = await services.current.get("reports").getBrandLiftReport(campaignId);
      setReportUrl(resp.data.url);
    } catch (e) {
      setError(
        e?.error?.message ||
          intl.formatMessage({
            id: "ERROR_RUNNING_REPORT",
            defaultMessage: "An error occurred while running the report.",
          }),
      );
    } finally {
      setLoading(false);
    }
  };

  if (!whitelabelBrandLiftCampaign) {
    return <Redirect to="/reports" />;
  }

  return (
    <Segment
      basic
      style={{ padding: "0" }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: "0.875rem" }}>
        <h1 style={{ marginBottom: 0 }}>
          <NavLink
            to="/reports"
            className="black"
            title={intl.formatMessage({ id: "HINT_BACK_REPORTS", defaultMessage: "Back to the Reports" })}
          >
            <Icon
              name="arrow left"
              size="small"
            />{" "}
            {intl.formatMessage({ id: "REPORT_BRAND_LIFT", defaultMessage: "Brand Lift Report" })}
          </NavLink>
        </h1>
      </div>
      <Divider hidden />
      {error && (
        <Message
          error
          content={error}
          style={{ marginBottom: "10px" }}
        />
      )}
      <Form autoComplete="off">
        <Form.Group
          inline
          style={{ alignItems: "center", flexWrap: "wrap" }}
        >
          <Form.Field className="filter-field">
            <label>{intl.formatMessage({ id: "LABEL_ADVERTISER", defaultMessage: "Advertiser" })}</label>
            <Dropdown
              placeholder={intl.formatMessage({ id: "HINT_ADVERTISER", defaultMessage: "Select advertiser" })}
              search
              selection
              clearable
              loading={advertisersLoading}
              options={advertisers}
              value={advertiserId}
              selectOnNavigation={false}
              onChange={(_e, { value }) => setAdvertiserId(value || null)}
            />
          </Form.Field>
          <Form.Field className="filter-field">
            <label>{intl.formatMessage({ id: "LABEL_CAMPAIGN", defaultMessage: "Campaign" })}</label>
            <Dropdown
              placeholder={intl.formatMessage({ id: "HINT_CAMPAIGN", defaultMessage: "Select campaign" })}
              search
              selection
              clearable
              loading={campaignsLoading}
              disabled={!advertiserId}
              options={campaigns}
              value={campaignId}
              selectOnNavigation={false}
              style={{ minWidth: "28rem" }}
              noResultsMessage={intl.formatMessage({
                id: "NO_BRAND_LIFT_CAMPAIGNS_FOR_ADVERTISER",
                defaultMessage: "No Brand Lift campaigns available for this advertiser.",
              })}
              onChange={(_e, { value }) => {
                setCampaignId(value || null);
                setReportUrl(null);
                setError(null);
              }}
            />
          </Form.Field>
          <Form.Field style={{ alignItems: "center" }}>
            <Button
              positive
              compact
              loading={loading}
              disabled={!campaignId || loading}
              onClick={handleViewReport}
            >
              {intl.formatMessage({ id: "BTN_VIEW_REPORT", defaultMessage: "View Report" })}
            </Button>
          </Form.Field>
        </Form.Group>
      </Form>
      {reportUrl ? (
        <div style={{ marginTop: "1.5rem" }}>
          <iframe
            src={reportUrl}
            title={intl.formatMessage({ id: "REPORT_BRAND_LIFT", defaultMessage: "Brand Lift Report" })}
            style={{ width: "100%", height: "80vh", border: "none" }}
          />
        </div>
      ) : (
        <Segment
          placeholder
          textAlign="center"
          style={{ marginTop: "1.5rem" }}
        >
          {intl.formatMessage({
            id: "EMPTY_BRAND_LIFT_REPORT",
            defaultMessage: "Select an advertiser and campaign above to view the Brand Lift report.",
          })}
        </Segment>
      )}
    </Segment>
  );
};

export default BrandLiftReportPage;
